import { useMyUser } from '@consigli/hooks';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/layouts/app-shell/app-shell';
import { formatFirstName } from '@/util/format-name';

import { NewProjectButton } from './new-project-button';
import { ProjectGrid } from './project-grid';
import { ProjectListEmpty } from './project-list-empty';
import {
  ProjectListLoading,
  ProjectListLoadingMore,
  ProjectListLoadingMoreLabel,
} from './project-list-loading';
import { ProjectListToolbar } from './project-list-toolbar';
import { useProjectList } from './use-project-list';
import { useProjectView } from './use-project-view';

export const ProjectListRedesignPage: FC = () => {
  const { user } = useMyUser();
  const { t } = useTranslation();

  const {
    projects,
    isLoading,
    isFetchingMore,
    isSearchPending,
    searchText,
    handleSearch,
    creatorFilter,
    setCreatorFilter,
    triggerRef,
  } = useProjectList();

  const [view, setView] = useProjectView();

  // navbar.menu.home, not navbar.breadcrumbs.home — that key is still
  // "All projects" for the old project list and project creation.
  return (
    <AppShell breadcrumbs={[{ label: t('navbar.menu.home') }]}>
      <div className="flex items-start justify-between gap-6">
        <div>
          {/* The design's `heading 3`. No `capitalize` — it corrupts a
              lowercase particle, turning "van der Berg" into "Van Der
              Berg". */}
          <h1
            className="text-2xl font-medium leading-[1.2] tracking-[-0.5px] text-[color:var(--ds-color-text-primary)]"
            data-testid="welcome-heading"
          >
            {t('project-list.greeting', {
              name: formatFirstName(user.firstName, user.lastName),
            })}
          </h1>
          <p
            className="pt-1.5 text-sm text-[color:var(--ds-color-text-secondary)]"
            data-testid="project-overview-text"
          >
            {t('project-list.projects-intro')}
          </p>
        </div>
        <NewProjectButton className="shrink-0" />
      </div>

      <ProjectListToolbar
        creatorFilter={creatorFilter}
        onCreatorFilterChange={setCreatorFilter}
        onSearch={handleSearch}
        onViewChange={setView}
        searchText={searchText}
        view={view}
      />

      {isLoading || (projects.length === 0 && isSearchPending) ? (
        /* Not only first paint: isLoading resets on every search and filter
           change, and isSearchPending covers the debounce. */
        <div className="pb-8">
          <ProjectListLoading view={view} />
        </div>
      ) : (
        <>
          {projects.length > 0 && (
            <div className="pb-8">
              <ProjectGrid projects={projects} view={view}>
                {isFetchingMore && <ProjectListLoadingMore view={view} />}
              </ProjectGrid>

              {isFetchingMore && <ProjectListLoadingMoreLabel />}

              {/* Infinite scroll trigger, below the label so the label stays
                  part of the grid's flow. */}
              <div className="h-4" ref={triggerRef} />
            </div>
          )}
          {projects.length === 0 && !isLoading && (
            <div className="pb-8">
              <ProjectListEmpty creatorFilter={creatorFilter} searchText={searchText} />
            </div>
          )}
        </>
      )}
    </AppShell>
  );
};
